import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { type Envelope, type DomainListPagination, manticoreKeys, parseCoreRead } from './_shared';

// ============================================

export type DomainStatus =
  | 'active'
  | 'deploying'
  | 'suspended'
  | 'disabled'
  | 'pending'
  | string;

/** One vendor attachment of a domain (multi-CDN aware). */
export interface CDNBinding {
  binding_id: string;
  provider: string;
  provider_id: string;
  zone_id?: string;
  uid?: string;
  status?: string;
  enabled?: boolean;
}

export interface CDNDomain {
  id?: string;
  domain: string;
  /** Backend field name — the CNAME to point DNS at. Populated from cname_target. */
  cname_target?: string;
  /** Alias for cname_target kept for consumer compatibility. */
  cname?: string;
  origin?: string;
  status?: DomainStatus;
  enabled?: boolean;
  tags?: string[];
  /** Every CDN binding of this domain (one per provider). */
  bindings?: CDNBinding[];
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface PaginatedDomainList {
  data: CDNDomain[];
  pagination: DomainListPagination;
}

// ---- Cache ----
export interface CacheKeyConfig {
  query_strategy: string;
  query_params: string[];
  query_sort: boolean;
  header_include: string[];
  cookie_include: string[];
  device_type: boolean;
  geo: boolean;
  lang: boolean;
}

export interface CacheVaryConfig {
  enabled: boolean;
  allowlist: string[];
  normalize_accept_encoding: boolean;
}

export interface CacheConfig {
  enabled?: boolean;
  bypass_cache?: boolean;
  ignore_client_no_cache?: boolean;
  default_ttl_secs?: number;
  max_ttl_secs?: number;
  edge_ttl_secs?: number;
  browser_ttl_secs?: number;
  stale_while_revalidate_secs?: number;
  stale_if_error_secs?: number;
  status_ttl?: Record<string, number>;
  status_browser_ttl?: Record<string, number>;
  cache_key?: CacheKeyConfig | null;
  vary?: CacheVaryConfig | null;
  [key: string]: unknown;
}

// ---- TLS / Certs ----
export interface TLSConfig {
  min_version?: string;
  http2_enabled?: boolean;
  http3_enabled?: boolean;
  auto_https_redirect?: boolean;
  hsts?: {
    enabled?: boolean;
    max_age_secs?: number;
    include_subdomains?: boolean;
    preload?: boolean;
  };
  [key: string]: unknown;
}

export interface CertDetail {
  id: number;
  domain: string;
  label?: string;
  issuer?: string;
  subject?: string;
  not_before?: string;
  not_after?: string;
  fingerprint_sha256?: string;
  is_active?: boolean;
  source?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

// Cert list is provider-keyed: { zerossl: CertDetail[], letsencrypt: [...] }.
export type CertsByProvider = Record<string, CertDetail[]>;

// ---- Mutation payloads ----
/**
 * Mirrors @mgmt CreateRequest (modules/service/cdn/domain/types.go): the origin
 * lives in a nested `upstream` block (CDNOriginConfig) and TLS policy in `tls` —
 * flat `origin`/`ssl_mode` fields are silently dropped by the backend binder.
 */
export interface CreateDomainUpstream {
  urls: string[];
  protocol?: string;
  selector?: string;
  host_header?: string;
  tls_to_origin?: boolean;
  origin_tls_verify?: boolean;
  [key: string]: unknown;
}

export interface CreateDomainRequest {
  domain: string;
  provider_id?: string;
  upstream?: CreateDomainUpstream;
  tls?: { min_version?: string; [key: string]: unknown };
  cache?: Record<string, unknown>;
}

export interface UpdateDomainRequest {
  enabled?: boolean;
  [key: string]: unknown;
}

export interface UploadCertRequest {
  cert_pem: string;
  key_pem: string;
  chain_pem?: string;
  label?: string;
  activate?: boolean;
}

const PER_PAGE_DEFAULT = 500;

// ============================================
// Domains
// ============================================

export interface DomainListQuery {
  page: number; // 1-based
  perPage: number;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  enabled?: boolean;
  /** Scope to domains assigned to this project (project_resources mapping). */
  projectId?: string;
  /** Scope to domains in any project owned by this team. */
  teamId?: string;
  /** Scope to domains carrying ANY of these tags (use `__untagged__` for none). */
  tags?: string[];
  /** Scope to domains whose status is ANY of these. */
  statuses?: string[];
  /** created_at lower/upper bound (YYYY-MM-DD, inclusive). */
  createdFrom?: string;
  createdTo?: string;
}

/**
 * Server-paginated + server-searched domain list (list page). Keyed by every
 * query param so each page/search/sort combo gets its own cache slot. With
 * 2000+ domains we can't load all + filter client-side, so the BE does it.
 */
export function useDomainListPaged(q: DomainListQuery) {
  return useQuery({
    queryKey: manticoreKeys.domainsPaged(q),
    enabled: q.enabled ?? true,
    // Keep the previous page visible while the next loads — avoids a
    // flash-to-empty on every keystroke / page change.
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: async (): Promise<PaginatedDomainList> => {
      const params = new URLSearchParams();
      params.set('page', String(q.page));
      params.set('per_page', String(q.perPage));
      if (q.search) params.set('search', q.search);
      if (q.sort) params.set('sort', q.sort);
      if (q.order) params.set('order', q.order);
      if (q.projectId) params.set('project_id', q.projectId);
      if (q.teamId) params.set('team_id', q.teamId);
      (q.tags ?? []).forEach((t) => params.append('tags', t));
      (q.statuses ?? []).forEach((s) => params.append('status', s));
      if (q.createdFrom) params.set('created_from', q.createdFrom);
      if (q.createdTo) params.set('created_to', q.createdTo);
      const res = await serviceApiV3.get<Envelope<CDNDomain[]>>(
        `/cdn/domains?${params.toString()}`,
      );
      return {
        data: res.data?.data ?? [],
        pagination: res.data?.pagination ?? { page: q.page, per_page: q.perPage, total: 0 },
      };
    },
  });
}

/** Load-all domains (analytics / overview that need the whole list at once). */
export function useDomainListAll() {
  return useQuery({
    queryKey: manticoreKeys.domainsAll,
    retry: false,
    queryFn: async (): Promise<CDNDomain[]> => {
      const res = await serviceApiV3.get<Envelope<CDNDomain[]>>(
        `/cdn/domains?per_page=${PER_PAGE_DEFAULT}`,
      );
      return res.data?.data ?? [];
    },
  });
}

export function useDomainDetail(domain: string | undefined) {
  return useQuery({
    queryKey: manticoreKeys.domain(domain ?? ''),
    enabled: !!domain,
    retry: false,
    // Status transitions (active/deploying/pending) during deployment — a 15s
    // poll surfaces state changes without hammering the API.
    refetchInterval: 15_000,
    queryFn: async (): Promise<CDNDomain | null> => {
      const res = await serviceApiV3.get<Envelope<CDNDomain>>(`/cdn/domains/${domain}`);
      const d = res.data?.data ?? null;
      // Backend returns `cname_target`; populate `cname` alias so existing
      // consumers (display fields) continue to work without change.
      if (d && d.cname_target && !d.cname) d.cname = d.cname_target;
      return d;
    },
  });
}

function useDomainInvalidation() {
  const qc = useQueryClient();
  return {
    // Org-wide lists key under ['cdn','domains',…]; service-scoped consoles key
    // under ['cdn','service',<id>,'domains',…]. A create/update/delete can be
    // triggered from either console, so refresh both trees — otherwise a domain
    // deleted from a service console lingers in its list until a hard reload.
    list: () => {
      qc.invalidateQueries({ queryKey: ['cdn', 'domains'] });
      qc.invalidateQueries({ queryKey: ['cdn', 'service'] });
    },
    domain: (domain: string) => qc.invalidateQueries({ queryKey: manticoreKeys.domain(domain) }),
  };
}

export function useCreateDomain(
  options?: UseMutationOptions<CDNDomain, unknown, CreateDomainRequest>,
) {
  const inv = useDomainInvalidation();
  return useMutation({
    mutationFn: async (body: CreateDomainRequest) => {
      const res = await serviceApiV3.post<Envelope<CDNDomain>>('/cdn/domains', body);
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Service-scoped create: POST /services/:id/cdn/domains. The BE binds the
 * domain to the service family's CORE (vEdge) provider — no org-wide
 * default-provider guessing. Preferred over useCreateDomain whenever the
 * caller knows which CDN service (umbrella) the domain belongs to.
 */
export function useCreateDomainForService(
  serviceId: string,
  options?: UseMutationOptions<CDNDomain, unknown, CreateDomainRequest>,
) {
  const inv = useDomainInvalidation();
  return useMutation({
    mutationFn: async (body: CreateDomainRequest) => {
      const res = await serviceApiV3.post<Envelope<CDNDomain>>(
        `/services/${serviceId}/cdn/domains`,
        body,
      );
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateDomain(
  options?: UseMutationOptions<CDNDomain, unknown, { domain: string } & UpdateDomainRequest>,
) {
  const inv = useDomainInvalidation();
  return useMutation({
    mutationFn: async ({ domain, ...body }) => {
      const res = await serviceApiV3.put<Envelope<CDNDomain>>(`/cdn/domains/${domain}`, body);
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      inv.domain(args[1].domain);
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteDomain(options?: UseMutationOptions<void, unknown, string>) {
  const inv = useDomainInvalidation();
  return useMutation({
    mutationFn: async (domain: string) => {
      await serviceApiV3.delete(`/cdn/domains/${domain}`);
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      options?.onSuccess?.(...args);
    },
  });
}

export function usePurgeDomainCache(options?: UseMutationOptions<unknown, unknown, string>) {
  return useMutation({
    mutationFn: async (domain: string) => {
      const res = await serviceApiV3.post(`/cdn/domains/${domain}/purge`, { mode: 'all', values: [] });
      return res.data;
    },
    ...options,
  });
}

// ============================================
// Cache config  (GET/PUT/DELETE /cdn/domains/:d/cache)
// ============================================

export function useCacheConfig(domain: string | undefined) {
  return useQuery({
    queryKey: manticoreKeys.cache(domain ?? ''),
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<CacheConfig | null> => {
      try {
        const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/cache`);
        // BE returns the core-read envelope: `{ core, core_provider, vendors }`.
        // `core` is the canonical (vEdge) binding's cache config — the single
        // source of truth. `vendors` carries per-binding read outcomes.
        // Falls back to legacy bare vendor map automatically via parseCoreRead.
        const { core } = parseCoreRead<CacheConfig>(res.data?.data);
        return core;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null; // never configured
        throw e;
      }
    },
  });
}

export function useUpdateCacheConfig(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, CacheConfig>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (config: CacheConfig) => {
      const res = await serviceApiV3.put(`/cdn/domains/${domain}/cache`, config);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.cache(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteCacheConfig(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await serviceApiV3.delete(`/cdn/domains/${domain}/cache`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.cache(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// TLS  (GET/PUT/DELETE /cdn/domains/:d/tls)
// ============================================

/** Unwrap a vendor-keyed response: `{ raven: { ...cfg } }` → `{ ...cfg }`. */
function unwrapTLS(raw: unknown): TLSConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const vals = Object.values(raw as object);
  // `{}` (no vendor key) = TLS never configured → null, not an empty config.
  if (vals.length === 0) return null;
  if (vals.length === 1 && vals[0] && typeof vals[0] === 'object') return vals[0] as TLSConfig;
  return raw as TLSConfig;
}

export function useTLSConfig(domain: string | undefined) {
  return useQuery({
    queryKey: manticoreKeys.tls(domain ?? ''),
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<TLSConfig | null> => {
      try {
        const res = await serviceApiV3.get<Envelope<unknown>>(`/cdn/domains/${domain}/tls`);
        return unwrapTLS(res.data?.data);
      } catch (e) {
        // 400/404 on GET means vendor never configured TLS → resolve null so
        // callers can show a "Pending Activation" placeholder, not an error.
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

export function useUpdateTLSConfig(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, TLSConfig>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (config: TLSConfig) => {
      const res = await serviceApiV3.put(`/cdn/domains/${domain}/tls`, config);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.tls(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteTLSConfig(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await serviceApiV3.delete(`/cdn/domains/${domain}/tls`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.tls(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Certificates  (/cdn/domains/:d/certs + /cdn/certs)
// ============================================

export function useCertList(domain: string | undefined) {
  return useQuery({
    queryKey: manticoreKeys.certs(domain ?? ''),
    enabled: !!domain,
    retry: false,
    queryFn: async (): Promise<CertsByProvider> => {
      const res = await serviceApiV3.get<Envelope<CertsByProvider>>(`/cdn/domains/${domain}/certs`);
      return res.data?.data ?? {};
    },
  });
}

/** Normalize PEM input: turn literal "\n" (2-char) into real newlines. */
function normalizePEM(pem: string): string {
  return pem.replace(/\\n/g, '\n').trim();
}

export function useUploadCert(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, UploadCertRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: UploadCertRequest) => {
      const body: Record<string, unknown> = {
        cert_pem: normalizePEM(req.cert_pem),
        key_pem: normalizePEM(req.key_pem),
        activate: req.activate,
      };
      if (req.chain_pem) body.chain_pem = normalizePEM(req.chain_pem);
      if (req.label) body.label = req.label;
      const res = await serviceApiV3.post(`/cdn/domains/${domain}/certs`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.certs(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteCert(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (certId: string) => {
      const res = await serviceApiV3.delete(`/cdn/domains/${domain}/certs/${certId}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.certs(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useActivateCert(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (certId: string) => {
      const res = await serviceApiV3.post(`/cdn/domains/${domain}/certs/${certId}/activate`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.certs(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Trigger an ACME issue (HTTP-01 / DNS-01) via ZeroSSL / Let's Encrypt. */
export function useIssueCert(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, { provider: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ provider }: { provider: string }) => {
      const res = await serviceApiV3.post(`/cdn/domains/${domain}/certs/acme`, { provider });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.certs(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Distribute a Tiger (managed SSL inventory) certificate onto this CDN domain.
 * Posts `{ cert_id }` to `…/certs/tiger`; @mgmt forwards the user's JWT to Tiger,
 * fetches the bundle (private key stays server-side) and installs the PEM on the
 * CDN edge for this domain. Mirrors `useDistributeTigerCertToWaap` — same
 * cert-list invalidation so the installed-certs card refreshes.
 */
export function useDistributeTigerCertToCdn(
  domain: string,
  options?: UseMutationOptions<unknown, unknown, { cert_id: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { cert_id: string }) => {
      const res = await serviceApiV3.post(`/cdn/domains/${domain}/certs/tiger`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: manticoreKeys.certs(domain) });
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Tags ----

/** Distinct tags across the org's CDN domains — feeds the tag filter. */
export function useDomainTags() {
  return useQuery({
    queryKey: manticoreKeys.domainTags,
    queryFn: async (): Promise<string[]> => {
      const res = await serviceApiV3.get<Envelope<string[]>>('/cdn/domains/tags');
      return res.data?.data ?? [];
    },
  });
}

/** Replace the tag set on a domain (PUT /cdn/domains/:id/tags). */
export function useUpdateDomainTags(
  options?: UseMutationOptions<string[], unknown, { domainId: string; tags: string[] }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domainId, tags }) => {
      const res = await serviceApiV3.put<Envelope<string[]>>(`/cdn/domains/${domainId}/tags`, { tags });
      return res.data?.data ?? tags;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['cdn', 'domains'] });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Move domain to another service (PUT /cdn/domains/:id/reassign)
// ============================================

export interface ReassignDomainResult {
  binding_id: string;
  domain_id: string;
  provider: string;
  from_service_id: string;
  to_service_id: string;
}

/**
 * Move a domain's CDN binding to another service of the SAME organization and
 * SAME provider. The target service selects which provider binding moves; the
 * binding id stays stable so analytics history follows. The backend rejects
 * cross-org/cross-provider targets (403 / 404) — surface those to the user.
 */
export function useReassignDomain(
  options?: UseMutationOptions<ReassignDomainResult, unknown, { domainId: string; serviceId: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domainId, serviceId }) => {
      const res = await serviceApiV3.put<Envelope<ReassignDomainResult>>(
        `/cdn/domains/${domainId}/reassign`,
        { service_id: serviceId },
      );
      return res.data?.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['cdn', 'domains'] });
      // Service-scoped domain lists key under ['cdn','service',<id>,...] — both
      // the source and the destination console must refetch after a move.
      qc.invalidateQueries({ queryKey: ['cdn', 'service'] });
      options?.onSuccess?.(...args);
    },
  });
}
